import { useCookies } from "@vueuse/integrations/useCookies";
import { defineStore } from "pinia";
import { useRiseIdStore } from "./../riseId";
import { useSessionStore } from "./../session";
import { useWalletStore } from "./../wallet";
import { useClerkUserStore } from "./user";
import { reportAuthIssue } from "@/utils/authDiagnostics";

export type ClerkError = {
  code: "authentication_invalid";
  longMessage: string;
  message: string;
};

let signOutPromise: Promise<void> | null = null;

export const useClerkSessionStore = defineStore("clerkSession", {
  state: (): { cleared: boolean } => ({
    cleared: false,
  }),
  getters: {
    needSecondFactor() {
      if (!this.$clerk?.client) return false;
      return this.$clerk.client.signIn.status === "needs_second_factor";
    },
    sessionId(): string {
      if (!this.$clerk?.client?.sessions[0]) return "";
      if (!localStorage) return "";
      if (!localStorage.getItem("clerk-session-id")) return "";

      return localStorage.getItem("clerk-session-id") as string;
    },
    async jwt() {
      if (!this.$clerk.session) return "";

      const jwt = await this.$clerk.session?.getToken({
        template: "User_Template",
      });
      if (!jwt) return "";

      return `Bearer ${jwt}`;
    },
  },
  actions: {
    isConnected(): boolean {
      const clerkUserStore = useClerkUserStore();
      return !!clerkUserStore.user?.primaryEmailAddress;
    },
    hasSession() {
      const clientSession = this.$clerk?.client?.sessions[0];
      return !this.cleared && clientSession;
    },
    async refreshSession() {
      const clientSession = this.$clerk?.client?.sessions[0];
      if (!clientSession) return;
      try {
        await this.$clerk.setSession(clientSession);
        localStorage.setItem("clerk-session-id", clientSession.id);
      } catch (e) {
        const clerkError: { errors: ClerkError[] } | any = e;
        if (clerkError?.errors?.[0]?.code === "authentication_invalid") {
          reportAuthIssue(
            "clerk_refresh_session_authentication_invalid",
            {
              hasClientSession: Boolean(clientSession),
              clientSessionId: clientSession?.id || null,
            },
            e,
          );
          await this.$router.replace("/sign-out");
        }
        else {
          reportAuthIssue(
            "clerk_refresh_session_failed",
            {
              hasClientSession: Boolean(clientSession),
              clientSessionId: clientSession?.id || null,
            },
            e,
          );
        }

        throw new Error((e as Error).message);
      }
    },
    async signOut() {
      if (signOutPromise) return signOutPromise;

      signOutPromise = (async () => {
        this.cleared = true;
        const { remove } = useCookies([]);

        if (this.$clerk) {
          await this.$clerk.signOut().catch((error) => {
            reportAuthIssue("clerk_sign_out_failed", {}, error);
          });
        }

        if (this.$clerk?.client) {
          await this.$clerk.client.destroy().catch((error) => {
            reportAuthIssue("clerk_client_destroy_failed_after_sign_out", {}, error);
          });
        }

        const walletStore = useWalletStore();
        walletStore.DISCONNECT();

        const sessionStore = useSessionStore();

        sessionStore.user = null;
        sessionStore.activeCompany = null;

        const riseIdStore = useRiseIdStore();
        riseIdStore.CLEAR_STATE();

        await remove("rise-user");
        await remove("__session");
        await remove("__clerk_db_jwt");
        await remove("__client_uat");
        await localStorage.removeItem("clerk-session-id");
        await localStorage.removeItem("vuex");
      })().finally(() => {
        signOutPromise = null;
      });

      return signOutPromise;
    },
  },
});
